import { useMutation } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import {
  Button,
  ColDefBuilder,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  makeDefaultSortableColumnDef,
  toast,
} from '@eluve/components';
import { EluveEditor, EluveEditorHandle } from '@eluve/eluve-editor';
import {
  PdfDocumentTabContent,
  updateDocumentReferenceContentMutation,
  updateDocumentReferenceTitleMutation,
} from '@eluve/frontend-feature-appointment';
import { graphql } from '@eluve/graphql.tada';

import { ArtifactHeader } from './ArtifactHeader';

export const patientDocumentsFragment = graphql(`
  fragment PatientDocuments on Patients @_unmask {
    __typename
    id
    documents(orderBy: { createdAt: DESC }) {
      __typename
      id
      title
      status
      docType
      content
      contentType
      authoredAt
      createdAt
    }
  }
`);

type DocumentRow = {
  id: string;
  title: string;
  docType: string;
  content: string;
  contentType: string;
  createdAt: string;
};

const DocumentPdfTab: React.FC<{ content: string; title: string }> = ({
  content,
  title,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      setIsLoading(true);
      setError(false);
      try {
        const { generateDocumentPdf } = await import('@eluve/pdf-rendering');
        const blob = await generateDocumentPdf(title, content);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setError(true);
          toast.error('Failed to generate PDF preview');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [content, title]);

  return (
    <Stack justify="center" className="h-[70vh]" gap={2} direction="horizontal">
      {isLoading && <Text color="tertiary">Generating PDF...</Text>}
      {error && <Text color="tertiary">Failed to generate PDF preview.</Text>}
      {pdfUrl && (
        <iframe src={pdfUrl} className="size-full rounded-sm border" />
      )}
    </Stack>
  );
};

type DocumentTabValue = 'markdown' | 'pdf';

const DocumentContentDialog: React.FC<{
  initialTitle: string;
  initialContent: string;
  onSave: (edited: { title: string; content: string }) => Promise<void>;
}> = ({ initialTitle, initialContent, onSave }) => {
  const [open, setOpen] = useState(false);
  const [localTitle, setLocalTitle] = useState(initialTitle);
  const [currentContent, setCurrentContent] = useState(initialContent);
  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<DocumentTabValue>('markdown');
  const editorRef = useRef<EluveEditorHandle | null>(null);

  useEffect(() => {
    if (open) {
      setLocalTitle(initialTitle);
      setCurrentContent(initialContent);
      setTitleError('');
      setContentError('');
      setShowDiscardConfirm(false);
      setActiveTab('markdown');
    }
  }, [open, initialTitle, initialContent]);

  const isDirty =
    localTitle !== initialTitle || currentContent !== initialContent;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    setOpen(nextOpen);
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    setOpen(false);
  };

  const handleEditorUpdate = () => {
    const content = editorRef.current?.getMarkdown() ?? '';
    setCurrentContent(content);
    if (contentError) setContentError('');
  };

  const handleSave = async () => {
    const trimmedTitle = localTitle.trim();
    const content = editorRef.current?.getMarkdown() ?? '';

    let hasError = false;
    if (!trimmedTitle) {
      setTitleError('Title cannot be empty.');
      hasError = true;
    }
    if (!content.trim()) {
      setContentError('Document content cannot be empty.');
      hasError = true;
    }
    if (hasError) return;

    setIsSaving(true);
    try {
      await onSave({ title: trimmedTitle, content });
      setOpen(false);
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <button
            type="button"
            className="cursor-pointer text-left hover:underline"
          >
            {initialTitle}
          </button>
        </DialogTrigger>
        <DialogContent
          size="4xl"
          className="flex max-h-[90vh] flex-col overflow-hidden"
        >
          <DialogHeader>
            {activeTab === 'markdown' ? (
              <DialogTitle asChild>
                <Input
                  value={localTitle}
                  onChange={(e) => {
                    setLocalTitle(e.target.value);
                    if (titleError) setTitleError('');
                  }}
                  placeholder="Document title"
                  aria-label="Document title"
                />
              </DialogTitle>
            ) : (
              <DialogTitle className="text-lg font-semibold">
                {localTitle}
              </DialogTitle>
            )}
            {titleError && activeTab === 'markdown' && (
              <Text size="xs" className="text-text-negative">
                {titleError}
              </Text>
            )}
          </DialogHeader>

          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as DocumentTabValue)}
            className="flex flex-1 flex-col overflow-hidden"
          >
            <TabsList>
              <TabsTrigger value="markdown">Markdown</TabsTrigger>
              <TabsTrigger value="pdf">PDF</TabsTrigger>
            </TabsList>
            <TabsContent
              value="markdown"
              className="flex-1 overflow-y-auto px-1 py-4"
            >
              <Stack gap={4}>
                <EluveEditor
                  ref={editorRef}
                  content={initialContent}
                  onUpdate={handleEditorUpdate}
                />
                {contentError && (
                  <Text size="xs" className="text-text-negative">
                    {contentError}
                  </Text>
                )}
              </Stack>
            </TabsContent>
            <TabsContent
              value="pdf"
              className="flex-1 overflow-y-auto px-1 py-4"
            >
              {activeTab === 'pdf' && (
                <DocumentPdfTab content={currentContent} title={localTitle} />
              )}
            </TabsContent>
          </Tabs>

          {activeTab === 'markdown' && (
            <DialogFooter>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenChange(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving || !isDirty}
              >
                Save changes
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDiscardConfirm} onOpenChange={setShowDiscardConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Discard unsaved edits?</DialogTitle>
            <DialogDescription>Your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDiscardConfirm(false)}
            >
              Keep editing
            </Button>
            <Button size="sm" onClick={handleDiscardConfirm}>
              Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Read-only viewer for file-backed PDF documents (content_type
 * application/pdf). Their body lives in a stored file, not in `content`, so
 * they must not open in the markdown editor — that shows a blank editor and
 * any save would violate the document_references content-type CHECK.
 */
const PdfDocumentDialog: React.FC<{ documentId: string; title: string }> = ({
  documentId,
  title,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="cursor-pointer text-left hover:underline"
        >
          {title}
        </button>
      </DialogTrigger>
      <DialogContent
        size="4xl"
        className="flex max-h-[90vh] flex-col overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
        </DialogHeader>
        <div className="h-[70vh]">
          <PdfDocumentTabContent documentId={documentId} title={title} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export interface PatientDocumentsProps {
  patientId: string;
  tenantId: string;
  hideHeader?: boolean;
}

export const PatientDocuments: React.FC<PatientDocumentsProps> = ({
  patientId,
  tenantId,
  hideHeader = false,
}) => {
  const { documents } = useCompleteFragment({
    fragment: patientDocumentsFragment,
    key: { id: patientId },
  });

  const [updateContent] = useMutation(updateDocumentReferenceContentMutation);
  const [updateTitle] = useMutation(updateDocumentReferenceTitleMutation);

  const handleEditSave = useCallback(
    async (doc: DocumentRow, edited: { title: string; content: string }) => {
      const mutations: Promise<unknown>[] = [];

      if (edited.title !== doc.title) {
        mutations.push(
          updateTitle({
            variables: { tenantId, id: doc.id, title: edited.title },
          }),
        );
      }
      if (edited.content !== doc.content) {
        mutations.push(
          updateContent({
            variables: { tenantId, id: doc.id, content: edited.content },
          }),
        );
      }

      if (mutations.length > 0) {
        await Promise.all(mutations);
        toast.success('Document updated');
      }
    },
    [tenantId, updateTitle, updateContent],
  );

  const columns = useMemo(
    () =>
      new ColDefBuilder<DocumentRow>()
        .colDef({
          ...makeDefaultSortableColumnDef('title', 'Title'),
          id: 'title',
          cell: ({ row }) =>
            row.original.contentType === 'application/pdf' ? (
              <PdfDocumentDialog
                documentId={row.original.id}
                title={row.original.title}
              />
            ) : (
              <DocumentContentDialog
                initialTitle={row.original.title}
                initialContent={row.original.content}
                onSave={(edited) => handleEditSave(row.original, edited)}
              />
            ),
        })
        .defaultSortable('docType', 'Type')
        .dateSortable('createdAt', 'Date')
        .build(),
    [handleEditSave],
  );

  const rows = (documents ?? []).map<DocumentRow>((doc) => ({
    id: doc.id,
    title: doc.title ?? '',
    docType: doc.docType
      ? doc.docType
          .split('-')
          .map((s) => capitalize(s))
          .join(' ')
      : '',
    content: doc.content ?? '',
    contentType: doc.contentType ?? 'text/markdown',
    createdAt: doc.createdAt ?? '',
  }));

  return (
    <Stack gap={3}>
      {!hideHeader && <ArtifactHeader text="Documents" />}
      <DataTable
        columns={columns}
        data={rows}
        initialSortingState={[
          {
            id: 'createdAt',
            desc: true,
          },
        ]}
      />
    </Stack>
  );
};
